/* eslint-disable max-len */
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DynamicContentModalComponent } from '@Work/Accounts/proprietary/dynamic-content-outlet/dynamic-content-modal.component';
import { AppService } from '@Core/services/proprietary/app.service';
import { ConfirmationDialogService } from '@Core/services/proprietary/confirmation-dialog/confirmation-dialog.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { Datasource, DataSourceStatus } from '@Core/services/proprietary/datasource';
import { ObjectService } from '@Core/services/proprietary/object.service';
import { cloneDeep } from 'src/ep-utils';
import { PageObjectService } from '../page/page-object.service';
import { TaskObjectService } from '../task/task-object.service';
import {
    InitializeTransaction,
    PrepareRequest,
    ResetState,
    SetConfigInfo,
    SetObjectState,
    SetPageMode
} from './interface-object.actions';
import { denormalize, normalize, rootSchema } from './interface-object.normalize';
import { InterfaceObjectState } from './interface-object.state';
import { EventType, InterfaceObject } from './interface-object.types';

@Injectable()
export class InterfaceObjectService implements OnDestroy {
    interfaceDialogModalRef: NgbModalRef;

    dsInterfaceGet: Datasource;
    dsInterfaceProcess: Datasource;
    dsInterfaceTaskCheck: Datasource;

    private dsInterfaceGetStatusSub: Subscription;
    private dsInterfaceProcessStatusSub: Subscription;
    private interfaceStatusCheckSub: Subscription;
    private dsTaskStatusSub: Subscription;

    private objectID: any = null;
    private objectCode: any = null;
    private pageID: any = null;
    private pageMode: boolean = false;
    private interfaceDialogOpen: boolean = false;
    private existingRecord: boolean = false;
    private taskID: any = null;
    private projectID: any = null;

    public interfaceComplete = new Subject();

    constructor(
        private store: Store,
        private app: AppService,
        private pageOS: PageObjectService,
        private taskOS: TaskObjectService,
        private modalService: NgbModal,
        private injector: Injector,
        private objectService: ObjectService,
        private confirmationDialog: ConfirmationDialogService,
        public dataService: DataService,
        private router: Router
    ) { }

    ngOnDestroy() {
        this.reset();
    }

    private getObjectConfig() {
        if (this.objectCode) {
            let objectConfig = this.objectService.getObjectConfig(this.objectCode);

            if (objectConfig) {
                this.store.dispatch(new SetConfigInfo(objectConfig));
            }
        }
    }

    public optionIncluded(optionName: string): boolean {
        let objectConfig = this.store.selectSnapshot(InterfaceObjectState.config);
        if (objectConfig && objectConfig.Set_Values) {
            let setValues = objectConfig.Set_Values.toLowerCase();
            return setValues.includes(optionName.toLowerCase());
        }

        return false;
    }

    public interfaceType(): string {
        let type = '';
        let objectConfig = this.store.selectSnapshot(InterfaceObjectState.config);
        if (objectConfig && objectConfig.Set_Values) {
            let setValues = objectConfig.Set_Values.toLowerCase();

            type = setValues.substring(setValues.indexOf('[') + 1, setValues.indexOf(']'));
        }

        return type;
    }

    public async openInterface(pageID: any, dataService?: DataService) {
        //Main entry point - from router guard.

        if (dataService) {
            this.dataService = dataService;
        } else {
            if (this.app.pageLevelDataService) {
                this.dataService = this.app.pageLevelDataService;
            }
        }

        this.dsInterfaceGet = this.dataService.addNewDatasource('Interface');
        this.dsInterfaceProcess = this.dataService.addNewDatasource('Interface');
        this.dsInterfaceTaskCheck = this.dataService.addNewDatasource('Task');

        let pageMode = this.app.pageParams.getParameter(pageID, 'PageMode');
        if (pageMode == 'Y') {
            this.loadPageMode(pageID); //Task header already loaded in App routing if PageMode = 'Y'
        } else {
            let interfaceID = this.app.pageParams.getParameter(pageID, 'Interface_ID');
            let objectCode = this.app.pageParams.getParameter(pageID, 'Object_Code');
            let entityID = this.app.pageParams.getParameter(pageID, 'Entity_ID');
            let roleType = this.app.pageParams.getParameter(pageID, 'Role_Type');
            let contractID = this.app.pageParams.getParameter(pageID, 'Contract_ID');
            let contractLockGUID = this.app.pageParams.getParameter(pageID, 'Contract_Lock_GUID');
            this.taskID = this.app.pageParams.getParameter(pageID, 'Task_ID');

            if (interfaceID && interfaceID > '') {
                if (await this.isInterfaceResolved(interfaceID, objectCode)) {
                    this.app.pageParams.setParameter(pageID, 'PageMode', 'Y');
                    this.reset();
                    this.router.navigate(['interface', { PageID: pageID }]);
                } else {
                    this.openDialogForExistingNoneResolved(interfaceID, objectCode);
                }
            } else {
                let interfaceObject: InterfaceObject = {
                    Object_Code: objectCode
                };
                if (entityID > '' && roleType > '') {
                    interfaceObject.Entity = { Entity_ID: entityID, Role_Type: roleType };
                }
                if (contractID > '') {
                    interfaceObject.Contract = { Contract_ID: contractID, Lock_GUID: contractLockGUID || '' };
                }

                this.launchInterface(interfaceObject);
            }
        }
    }

    private async intefaceCompleted() {
        this.interfaceComplete.next('COMPLETE');
        if (this.existingRecord) {
            this.taskOS.loadTask(this.taskID, true);
        } else {
            //If not in an active project open project tracker for the project.
            if (!this.taskOS.isProjectActive && this.projectID && parseInt(this.projectID, 10) > 0) {
                this.taskOS.openProjectTrackerByProjectID(this.projectID);
            } else {
                //if launched from project tracker, then refresh page level dataservice.
                let currentRoute = this.router.url;
                if (currentRoute.startsWith('/project-tracker')) {
                    this.app.pageLevelDataService.refreshAll(false);
                }
            }
        }
        this.reset();
    }

    private loadPageMode(pageID: any) {
        this.pageMode = true;
        this.pageOS.setProtectedMode(true);
        this.store.dispatch(new SetPageMode());
        this.pageID = pageID;
        let interfaceID = this.app.pageParams.getParameter(pageID, 'Interface_ID');
        this.objectCode = this.app.pageParams.getParameter(pageID, 'Object_Code');
        this.getObjectConfig();
        this.loadObject(interfaceID);
    }

    private launchInterface(interfaceObject: InterfaceObject) {
        this.pageMode = false;
        this.objectCode = interfaceObject.Object_Code;
        this.getObjectConfig();
        this.objectID = '-1';
        // If new interface being created, but we already have a task (most likely WF step) open project tracker so we leave the previous step's page.
        if (this.taskID && parseInt(this.taskID, 10) > 0) {
            if (this.taskOS.isProjectActive) {
                this.taskOS.openProjectTracker();
            }

            this.existingRecord = true;
        }
        this.store.dispatch(new InitializeTransaction(interfaceObject)).subscribe((action) => {
            if (!this.optionIncluded('$AUTORUN$') || !this.optionIncluded('$AUTOACCEPT$')) {
                this.openInterfaceDialog();
            }

            if (this.optionIncluded('$AUTORUN$')) {
                this.process(EventType.Submit);
            }
        });
    }

    public submitInterface() {
        this.process(EventType.Submit);
    }

    private openDialogForExistingNoneResolved(interfaceID: string, objectCode: string) {
        this.pageMode = false;
        this.existingRecord = true;
        this.objectCode = objectCode;
        this.getObjectConfig();
        this.loadObject(interfaceID);
        this.openInterfaceDialog();
    }

    private loadObject(id: any) {
        this.objectID = id;
        if (this.dsInterfaceGetStatusSub) {
            this.dsInterfaceGetStatusSub.unsubscribe();
        }
        this.dsInterfaceGet.reset();

        this.dsInterfaceGet.get(
            { View: 'Full', json: 'CT2', Interface_GUID: id, Object_Code: this.objectCode },
            true,
            false
        );
        this.dsInterfaceGetStatusSub = this.dsInterfaceGet.status$.subscribe((status) => {
            this.dsInterfaceGetStatusUpdated(status);
        });
    }

    private dsInterfaceGetStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsInterfaceGetStatusSub) {
                    this.dsInterfaceGetStatusSub.unsubscribe();
                }

                this.objectID = this.dsInterfaceGet.objectNode.Interface_GUID;
                this.taskID = this.dsInterfaceGet.tranNode.Task_ID;
                this.projectID = this.dsInterfaceGet.tranNode.Session_ID;
                this.loadData(this.dsInterfaceGet.data);
                break;
            case DataSourceStatus.Error:
                if (this.dsInterfaceGetStatusSub) {
                    this.dsInterfaceGetStatusSub.unsubscribe();
                }
                break;
        }
    }

    private async loadData(data: any) {
        let normalizedData = normalize(data, rootSchema);
        this.store.dispatch(new SetObjectState(normalizedData.entities));
    }

    public reset() {
        // Remove Subscriptions
        if (this.dsInterfaceGetStatusSub) {
            this.dsInterfaceGetStatusSub.unsubscribe();
        }
        if (this.dsInterfaceProcessStatusSub) {
            this.dsInterfaceProcessStatusSub.unsubscribe();
        }
        if (this.interfaceStatusCheckSub) {
            this.interfaceStatusCheckSub.unsubscribe();
        }
        if (this.dsTaskStatusSub) {
            this.dsTaskStatusSub.unsubscribe();
        }

        // Delete Datasources
        if (this.dsInterfaceGet) {
            this.dataService.deleteDatasource(this.dsInterfaceGet);
        }
        if (this.dsInterfaceProcess) {
            this.dataService.deleteDatasource(this.dsInterfaceProcess);
        }
        if (this.dsInterfaceTaskCheck) {
            this.dataService.deleteDatasource(this.dsInterfaceTaskCheck);
        }

        if (this.interfaceDialogModalRef) {
            this.interfaceDialogModalRef.close();
        }

        // //Reset state and remaining properties
        this.store.dispatch(new ResetState());

        this.objectID = null;
        this.objectCode = null;
        this.pageID = null;
        this.pageMode = false;
        this.existingRecord = false;
        this.interfaceDialogOpen = false;
        this.taskID = null;
        this.projectID = null;
    }

    private process(event: EventType) {
        let tranNode: any = {
            Task_ID: this.taskID || '', //Handled differently since this is done in a dialog, not a page. The Current taskID in task state won't be for the Interface.
            Keep_Task_Open: this.pageOS.keepTaskOpen(event, [EventType.Accept, EventType.Reject], false),
            Event: event
        };

        this.store.dispatch(new PrepareRequest(tranNode)).subscribe((action) => this.finishProcess());
    }

    private finishProcess() {
        if (this.dsInterfaceProcessStatusSub) {
            this.dsInterfaceProcessStatusSub.unsubscribe();
        }
        let stateEntities = this.store.selectSnapshot(InterfaceObjectState.entities);
        stateEntities = cloneDeep(stateEntities);

        if (stateEntities.Interface && stateEntities.Interface['-1']) {
            if (stateEntities.Transaction[1].Event == EventType.Update) {
                stateEntities.Transaction[1].Event = EventType.Create;
            }
        }

        let tran = denormalize('1', rootSchema, stateEntities);

        this.dsInterfaceProcess.process({ json: 'CT2', Object_Code: this.objectCode }, tran);
        this.dsInterfaceProcessStatusSub = this.dsInterfaceProcess.status$.subscribe((status) => {
            this.dsProcessStatusUpdated(status);
        });
    }

    private async dsProcessStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsInterfaceProcessStatusSub) {
                    this.dsInterfaceProcessStatusSub.unsubscribe();
                }

                this.objectID = this.dsInterfaceProcess.objectNode.Interface_GUID;
                this.taskID = this.dsInterfaceProcess.tranNode.Task_ID;
                this.projectID = this.dsInterfaceProcess.tranNode.Session_ID;

                if (this.dsInterfaceProcess.tranNode.Event == EventType.Submit && this.interfaceType() == 'website') {
                    let url = this.dsInterfaceProcess.objectNode.URL;
                    if (url.startsWith('http')) {
                        window.open(url, '_blank', 'noreferrer');
                    }
                }

                if (await this.isTaskResolved(this.taskID)) {
                    this.intefaceCompleted();
                } else {
                    this.loadObject(this.objectID);
                }

                break;
            case DataSourceStatus.Error:
                if (this.dsInterfaceProcessStatusSub) {
                    this.dsInterfaceProcessStatusSub.unsubscribe();
                }
                if (this.interfaceDialogModalRef) {
                    this.interfaceDialogModalRef.close();
                }
                this.interfaceDialogOpen = false;
        }
    }

    private openInterfaceDialog() {
        if (!this.interfaceDialogOpen) {
            this.interfaceDialogOpen = true;
            let component: string = 'InterfaceDialogComponent';
            this.interfaceDialogModalRef = this.modalService.open(DynamicContentModalComponent, {
                centered: true,
                size: 'md',
                injector: this.injector,
                backdrop: 'static'
            });
            this.interfaceDialogModalRef.componentInstance.showCloseButton = false;
            this.interfaceDialogModalRef.componentInstance.type = component;
            this.interfaceDialogModalRef.componentInstance.dynamicData = {};
        }
    }

    public closeInterfaceDialog(action: 'Complete' | 'Accept' | 'Reject' | 'Cancel') {
        if (action == 'Cancel') {
            this.interfaceComplete.next('CANCEL');
            this.reset();
        } else {
            if (this.objectID && this.objectID > '') {
                if (this.interfaceStatusCheckSub) {
                    this.interfaceStatusCheckSub.unsubscribe();
                }
                this.dsInterfaceGet.reset();

                this.dsInterfaceGet.get(
                    { View: 'Full', json: 'CT2', Interface_GUID: this.objectID, Object_Code: this.objectCode },
                    true,
                    false
                );
                this.interfaceStatusCheckSub = this.dsInterfaceGet.status$.subscribe((status) => {
                    this.interfaceStatusCheckSubUpdated(action, status);
                });
            }
        }
    }

    private async interfaceStatusCheckSubUpdated(action: 'Complete' | 'Accept' | 'Reject', status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.interfaceStatusCheckSub) {
                    this.interfaceStatusCheckSub.unsubscribe();
                }

                this.objectID = this.dsInterfaceGet.objectNode.Interface_GUID;
                this.taskID = this.dsInterfaceGet.tranNode.Task_ID;
                this.projectID = this.dsInterfaceGet.tranNode.Session_ID;
                this.loadData(this.dsInterfaceGet.data);

                let status = this.dsInterfaceGet.objectNode?.Status;
                if (status == 'A' || status == 'R') {
                    if (await this.isTaskResolved(this.taskID)) {
                        this.intefaceCompleted();
                    }
                } else {
                    switch (action) {
                        case 'Accept':
                        case 'Reject':
                            this.process(EventType[action]);
                            break;

                        case 'Complete':
                            this.confirmationDialog
                                .confirm(
                                    'Manually Accept?',
                                    `${this.dsInterfaceGet.objectNode.Interface_Name} appears to still be processing. Do you want to manually Accept and continue without waiting for processing to complete?`,
                                    'Accept',
                                    'Cancel',
                                    'lg'
                                )
                                .then((confirmed) => {
                                    if (confirmed) {
                                        this.process(EventType.Accept);
                                    }
                                })
                                .catch(() => { });
                            break;
                    }
                }

                break;

            case DataSourceStatus.Error:
                if (this.interfaceStatusCheckSub) {
                    this.interfaceStatusCheckSub.unsubscribe();
                }
                break;
        }
    }

    private async isTaskResolved(taskID: string) {
        return new Promise(async (resolve) => {
            this.removeTaskSub();
            this.dsInterfaceTaskCheck.reset();

            this.dsInterfaceTaskCheck.get({ View: 'Full', json: 'CT2', Task_ID: taskID }, true, false);

            this.dsTaskStatusSub = this.dsInterfaceTaskCheck.status$
                .pipe(filter((status) => [DataSourceStatus.Ready, DataSourceStatus.Error].includes(status)))
                .subscribe((status) => {
                    this.removeTaskSub();
                    switch (status) {
                        case DataSourceStatus.Ready:
                            if (this.dsInterfaceTaskCheck.objectNode.Resolved_Date) {
                                resolve(true);
                            } else {
                                resolve(false);
                            }
                            break;
                        case DataSourceStatus.Error:
                            resolve(false);
                            break;
                    }
                });
        });
    }

    private removeTaskSub() {
        if (this.dsTaskStatusSub) {
            this.dsTaskStatusSub.unsubscribe();
        }
    }

    private removeStatusCheckSub() {
        if (this.interfaceStatusCheckSub) {
            this.interfaceStatusCheckSub.unsubscribe();
        }
    }

    private async isInterfaceResolved(interfaceID: string, objectCode: string) {
        return new Promise(async (resolve) => {
            this.removeStatusCheckSub();
            this.dsInterfaceGet.reset();

            this.dsInterfaceGet.get(
                { View: 'Full', json: 'CT2', Interface_GUID: interfaceID, Object_Code: objectCode },
                true,
                false
            );

            this.interfaceStatusCheckSub = this.dsInterfaceGet.status$
                .pipe(filter((status) => [DataSourceStatus.Ready, DataSourceStatus.Error].includes(status)))
                .subscribe((status) => {
                    this.removeStatusCheckSub();
                    switch (status) {
                        case DataSourceStatus.Ready:
                            if (['A', 'R'].includes(this.dsInterfaceGet.objectNode.Status)) {
                                resolve(true);
                            } else {
                                resolve(false);
                            }
                            break;
                        case DataSourceStatus.Error:
                            resolve(false);
                            break;
                    }
                });
        });
    }
}
